import * as React from 'react';
import {HStack, Stack, VStack} from '../Stack';
import {Spacer} from '../Spacer';

import {useSize} from '../../themes';
import {
  InfoIcon,
  UpgradeIcon,
  CheckCircleIcon,
  CloseCircleIcon,
  WarningTriangleIcon,
  type SpringIconType,
} from '../../icons';
import {CloseButtonView, IconWrapperView, NoteView} from './Note.styles';
import {NoteTitle} from './NoteTitle';
import {NoteContext} from './useNote';
import {renderElement} from '../../utils';
import type {HTMLAttributes, SpringTooling} from '../__SharedTypes';

type IconType =
  | SpringIconType
  | React.ReactElement<React.ComponentProps<SpringIconType>>
  | React.ReactNode
  | React.ComponentType;

type NoteColorVariant =
  | 'primary'
  | 'danger'
  | 'default'
  | 'success'
  | 'upgrade'
  | 'urgent'
  | 'warning';

type NoteSize = 'medium' | 'x-large';

export type NoteProps = {
  /**
   * Content for Note.
   */
  children?: React.ReactNode;
  /**
   * A color to represent the intention of the Note.
   */
  colorVariant?: NoteColorVariant;
  /**
   * Renders a CloseButton for dismissal.
   */
  hasCloseButton?: boolean;
  /**
   * Determines if an accompanying icon should render.
   */
  hasIcon?: boolean;
  /**
   * A custom icon for the Note.
   */
  icon?: IconType;
  /**
   * Removes coloration from Note.
   */
  isGhost?: boolean;
  /**
   * Whether the NoteBody should render inline with its siblings.
   */
  isInline?: boolean;
  /**
   * @deprecated: Used when nested within a Select/Popover component in Designer.
   */
  isNested?: boolean;
  /**
   * Whether the padding should be removed from the Note.
   */
  isSeamless?: boolean;
  /**
   * Renders a tinted background for the Note based on the colorVariant.
   */
  isTinted?: boolean;
  /**
   * Callback function when the CloseButton is clicked (enabled with hasCloseButton).
   */
  onDismiss?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * The size of the Note.
   */
  size?: NoteSize;
  /**
   * Title content to render on top of content.
   */
  title?: React.ReactNode;
} & HTMLAttributes &
  SpringTooling;

const getAriaLive = (colorVariant: NoteColorVariant) => {
  switch (colorVariant) {
    case 'success':
      return 'polite';
    case 'urgent':
    case 'danger':
      return 'assertive';
    default:
      return undefined;
  }
};

const iconComponents = {
  primary: InfoIcon,
  danger: CloseCircleIcon,
  default: InfoIcon,
  success: CheckCircleIcon,
  upgrade: UpgradeIcon,
  urgent: InfoIcon,
  warning: WarningTriangleIcon,
} as const;

const NoteIcon = ({
  colorVariant,
  hasIcon = true,
  icon,
}: {
  colorVariant: NoteColorVariant;
  hasIcon: boolean;
  icon?: IconType;
}) => {
  if (!hasIcon) return null;

  const IconComponent = iconComponents[colorVariant] || iconComponents.default;

  return (
    <IconWrapperView>
      {icon ? renderElement(icon) : <IconComponent size={16} />}
    </IconWrapperView>
  );
};

const CloseButton = ({onDismiss}: {onDismiss: NoteProps['onDismiss']}) => {
  return <CloseButtonView onClick={onDismiss} />;
};

/**
 * # Note
 *
 * Notes informs users about important changes or conditions.
 *
 * ## Usage
 *
 * ```jsx
 * import {Note} from '@packages/systems/spring';
 *
 * const Example = () => {
 *   return <Note colorVariant="success">You did it!</Note>;
 * };
 * ```
 *
 * @component
 */
export const Note = React.forwardRef<HTMLDivElement, NoteProps>(
  (
    {
      children,
      colorVariant = 'primary',
      hasCloseButton,
      hasIcon = true,
      icon,
      isGhost,
      isInline = false,
      isSeamless = false,

      /**
       * Currently unsure about the desired dismissible pattern / setup
       * for all Webflow surfaces.
       *
       * @see https://github.com/webflow/webflow/pull/47239#pullrequestreview-971534574
       * @see https://webflow.atlassian.net/browse/PDTY-2561
       *
       * @SPRING_CLEANING
       */
      onDismiss,

      size: sizeProp,
      title,

      ...props
    }: NoteProps,
    ref
  ) => {
    const size = useSize(sizeProp);

    const isDanger = Boolean(colorVariant === 'danger');
    const ariaLive = getAriaLive(colorVariant);

    return (
      <NoteContext.Provider
        value={{
          inlineTitle: title && isInline ? title : undefined,
          isInline,
        }}
      >
        <NoteView
          aria-live={ariaLive}
          colorVariant={colorVariant}
          hasTitle={Boolean(title)}
          isGhost={isGhost}
          isInline={isInline}
          isSeamless={isSeamless}
          ref={ref}
          role={isDanger ? 'alert' : 'note'}
          size={size}
          {...props}
        >
          <HStack
            align={isInline ? 'center' : 'start'}
            spacing={size === 'x-large' ? 2 : 1}
          >
            <NoteIcon
              colorVariant={colorVariant}
              hasIcon={hasIcon}
              icon={icon}
            />
            <Spacer>
              <VStack spacing={size === 'x-large' ? 2 : 1}>
                {title && !isInline ? <NoteTitle>{title}</NoteTitle> : null}
                <Stack
                  align={isInline ? 'center' : 'start'}
                  data-automation-id="note-inner-container"
                  direction={isInline ? 'row' : 'column'}
                  justify={isInline ? 'space-between' : 'start'}
                  spacing={size === 'x-large' ? 2 : 1}
                >
                  {children}
                </Stack>
              </VStack>
            </Spacer>
            {hasCloseButton ? <CloseButton onDismiss={onDismiss} /> : null}
          </HStack>
        </NoteView>
      </NoteContext.Provider>
    );
  }
);

Note.displayName = 'Note';
Note.toString = NoteView.toString;

export default Note;
